import { useI18nStore } from '../../shared/i18n/i18nStore';
import { fmtCurrency } from '../../shared/lib/format';
import type { LadderRow } from '../../shared/milhar/rulesWindow';

/**
 * The payout ladder as a staircase: one row per hit count, the drawn number's
 * trailing digits lit, the prize on the right. Shared by the lobby card (compact)
 * and the Milhar "How it works" sheet (`size="lg"`), so the picture a player
 * learns in one place is the same picture everywhere.
 *
 * <p>Unmatched slots show a dot, never the drawn digit — printing it made every
 * row read as the same number at a different price. Every figure comes from
 * `payoutLadder`; the jackpot row shows the live pool because that tier has no
 * fixed multiplier. No drawn number yet → the lit cells stay blank rather than
 * carrying an invented one.
 */
export function MatchLadderRows({ digits, rows, drawn, jackpot, currency, size = 'sm' }: {
  digits: number;
  rows: LadderRow[];
  /** The last real result, exactly `digits` long, or null. */
  drawn: string | null;
  /** Live pool (takeHome) as a number, or null when unknown. */
  jackpot: number | null;
  currency: string;
  size?: 'sm' | 'lg';
}) {
  const t = useI18nStore((state) => state.t);
  const drawnDigits = drawn && drawn.length === digits ? drawn.split('') : null;
  return (
    <>
      <div className={`hn-lad-hd${size === 'lg' ? ' is-lg' : ''}`}>
        {/* The product's own phrase (milharPayoutHint), not a new one. */}
        <span>{t('milharPayoutHint')}</span>
        {drawnDigits ? <em>{t('hallNextLadderDrawn').replace('%n%', drawn!)}</em> : null}
      </div>
      <div className={`hn-lad-rows${size === 'lg' ? ' is-lg' : ''}`}>
        {rows.map((row) => {
          const isJackpot = row.matched === digits;
          const label = isJackpot
            ? t('hallNextHitsAll').replace('%n%', String(digits))
            : row.matched === 1
              ? t('hallNextHitsOne')
              : t('hallNextHits').replace('%n%', String(row.matched));
          const amount = isJackpot
            ? (jackpot == null ? '' : fmtCurrency(jackpot, currency))
            : (row.payout == null ? '' : fmtCurrency(row.payout, currency));
          return (
            <div key={row.matched} className={`hn-lad-row${isJackpot ? ' is-jack' : ''}`}>
              <span className="hn-lad-n">{label}</span>
              <span className="hn-lad-dg">
                {Array.from({ length: digits }, (_, index) => {
                  // Matching runs from the last digit backwards, so the lit
                  // cells are the trailing ones.
                  const hit = index >= digits - row.matched;
                  return (
                    <span key={index} className={`hn-lad-d${hit ? ' is-hit' : ''}`}>
                      {hit && drawnDigits ? drawnDigits[index] : '·'}
                    </span>
                  );
                })}
              </span>
              <span className="hn-lad-pay">{amount}</span>
            </div>
          );
        })}
      </div>
    </>
  );
}
